/**
 * Design tokens, and the two palettes they resolve to.
 *
 * THE DARK PALETTE IS UNCHANGED from the original prototype and must stay that way — those
 * values are the Lyrixa visual identity, not implementation detail. Batch 6 §6 added a light
 * theme beside it and is explicit that dark stays the DEFAULT and keeps its current
 * appearance exactly, so nothing in `DARK` moved.
 *
 * WHAT CHANGED, AND WHY IT HAD TO. `c` used to hold the dark hex values directly, and about
 * 1250 inline `style={{ color: c.text2 }}` props across 138 files read them. An inline style
 * prop outranks every rule in the stylesheet, so no amount of light-theme CSS could have
 * repainted those — the light theme would have been a stylesheet the app ignored. `c` now
 * holds `var(--lyx-*)` references instead, and the properties are declared twice in
 * global.css: on `:root` (dark) and under `[data-theme='light']`. Every one of those call
 * sites became theme-aware without being edited, and switching themes is one attribute on
 * <html>.
 *
 * So there are now three lists rather than two, and `theme.test.ts` ties them together: `c`
 * (the names), the `:root` block (their dark values, which must equal `DARK`) and the
 * `[data-theme='light']` block (which must equal `LIGHT`). A token added to one and forgotten
 * in another is a failing test rather than an element painted `transparent`.
 *
 * WHICH TEXT TOKEN TO USE
 * -----------------------
 * The values are fixed, but they are not interchangeable, and `text3` in particular was being
 * used for things people have to read. Measured against the surfaces it actually appears on
 * — dark first, light in brackets:
 *
 *              bg          surface1     surface2     surface3
 *   text1     16.1 (17.2)  15.1 (17.8)  13.9 (16.3)  12.4 (15.0)
 *   text2      7.1  (7.5)   6.7  (7.7)   6.1  (7.1)   5.5  (6.5)
 *   text3      3.3  (2.5)   3.1  (2.6)   2.8  (2.4)   2.5  (2.2)
 *
 * 4.5:1 is the threshold for text at these sizes. So, on BOTH themes:
 *
 *   text1  primary text — titles, values, field contents, active states.
 *   text2  ANY other text. Secondary copy, labels, captions, metadata,
 *          placeholders, hints, eyebrows, dates, costs, icons that carry meaning.
 *   text3  NOT for text, and not for a meaningful icon either — it clears 3:1
 *          only against `bg`, and on light it does not clear 3:1 anywhere.
 *          Reserve it for decoration that nothing depends on: hairlines,
 *          inactive tick marks, separators.
 *
 * text3 is deliberately no MORE readable on light than on dark. Raising it would have made
 * the rule "text3 is decoration" true on one theme and false on the other, and the whole
 * value of that rule is that a component author does not have to ask which theme they are on.
 *
 * Hierarchy below text2 is expressed with size, weight and spacing rather than by fading the
 * colour further, which is how the Studio's captions stay quiet while still being legible.
 * See `contrast.test.ts`, which asserts both halves of the table above.
 */
/**
 * THE DARK PALETTE — the app's default appearance, and the values that were previously `c`
 * itself.
 *
 * Unchanged, to the byte. Batch 6 §6 asks for a light theme AND for the dark one to look
 * exactly as it does today, so this object is the "exactly as it does today" half: every
 * number here is the prototype's, and `contrast.test.ts` still measures these rather than a
 * variable name.
 *
 * `accentFill` and `dangerFill` are new NAMES but not new colours — on this theme each is
 * the same value as the token it splits from, which is deliberate and is asserted in
 * `theme.test.ts`. The split only does anything on light; see `ACCENT AS INK VS ACCENT AS
 * FILL` under `LIGHT` for what it is for.
 */
export const DARK = {
  bg: '#101114',
  surface1: '#17181D',
  surface2: '#1E2026',
  surface3: '#262930',
  border: '#2A2D35',
  borderStrong: '#3A3E48',
  text1: '#EDEDEC',
  text2: '#9B9FA8',
  text3: '#63666F',
  accent: '#F2A93C',
  accentFill: '#F2A93C',
  accentSoft: 'rgba(242,169,60,0.12)',
  accentBorder: 'rgba(242,169,60,0.55)',
  success: '#7FD9A8',
  /*
    A token as of §6, and it was `'rgba(127,217,168,0.12)'` written out by hand at five
    call sites — the "Live" release badge, the licence status card, the publisher wizard's
    completed steps, the verification result and a closed support ticket. Identical to this
    value in every one of them, so it was already a token in everything but name; the only
    thing its not being one achieved was five soft mint washes that could not follow the
    theme. Named to match `accentSoft` and `dangerSoft`, which it sits beside in three of
    those five files.
  */
  successSoft: 'rgba(127,217,168,0.12)',
  danger: '#E8735F',
  dangerFill: '#E8735F',
  dangerSoft: 'rgba(232,115,95,0.12)',
} as const;

/**
 * THE LIGHT PALETTE.
 *
 * NOT AN INVERSION, which the brief rules out by name and which would not work anyway: the
 * dark theme's surfaces climb *towards* the light as they come forward (bg is the darkest,
 * surface3 the lightest), so inverting them would make a raised card DARKER than the page
 * behind it and reverse the whole elevation model. Here the ramp runs the other way — the
 * page is the brightest thing and each raised surface is a shade deeper — which is how a
 * light interface expresses the same hierarchy.
 *
 * HOW THE NEUTRALS WERE CHOSEN, since "do not introduce arbitrary colours" applies here too:
 * every grey is the same HUE as its dark counterpart — a blue-leaning neutral around 221°,
 * which is what makes the dark theme read as charcoal rather than as pure black — with the
 * lightness reversed and the saturation kept low. So the light theme is recognisably the same
 * product rather than a different one, and no new hue enters the palette.
 *
 * ACCENT AS INK VS ACCENT AS FILL — the one genuinely hard problem here, and the reason
 * there are two amber tokens rather than one.
 *
 * #F2A93C is the brand and it is 9.5:1 on the dark page, so on dark the same value can be
 * both the thing you write with and the thing you fill a button with. On white it is 2.0:1.
 * That is not a taste judgement: 111 of the ~170 accent call sites are TEXT or a meaningful
 * icon, and brand amber as light-theme body text is illegible, while the remaining 59 are
 * button fills, checked radios, slider tracks and progress bars where the amber is the whole
 * point and the ink on top of it is `#1A1300`.
 *
 * One token cannot be both, so the ROLE is what split:
 *
 *   accent      the amber you write with. Brand #F2A93C on dark; on light, #8F5A0A — the
 *               same hue (36°) and saturation (0.94) as the brand, darkened until it reads.
 *   accentFill  the amber you fill with. #F2A93C on BOTH themes, so the primary button,
 *               the checked radio and the progress bar are the brand colour everywhere and
 *               `#1A1300` stays the correct ink on them (9.3:1) without a second case.
 *
 * The brand colour is therefore not altered on either theme — it keeps every surface it
 * currently paints. What changed is that text stopped borrowing a fill colour.
 *
 * `danger` splits the same way and for the same reason (#E8735F is 3.1:1 on white), with
 * `dangerFill` keeping the original for ConfirmDialog's destructive button. `success` needs
 * no fill variant — it is never used as one — so it is simply darkened; #7FD9A8 is 1.6:1 on
 * white. Every hue is preserved in each case; only lightness moved.
 *
 * The two soft tints keep their hues and gain a little alpha, because 12% of amber over
 * white is nearly invisible where 12% over #101114 is a clear wash.
 *
 * `text2` is #4F535C rather than the lighter grey it started as because of the DISABLED
 * FADE: `DISABLED_OPACITY` multiplies a whole control, so a disabled ghost button's label is
 * text2 blended 80% into the surface behind it. At #5B606B that landed at 3.96:1 — the
 * colour was readable and the disabled state of it was not. This is the lightest value in
 * the family that survives the fade at 4.61:1.
 *
 * `contrast.test.ts` measures every token here against every surface it appears on, exactly
 * as it does the dark set.
 */
export const LIGHT = {
  bg: '#FBFBFC',
  surface1: '#FFFFFF',
  surface2: '#F4F5F7',
  surface3: '#EAECF0',
  border: '#DFE1E6',
  borderStrong: '#C3C7D0',
  text1: '#16181D',
  text2: '#4F535C',
  text3: '#9BA0AB',
  accent: '#8F5A0A',
  accentFill: '#F2A93C',
  accentSoft: 'rgba(242,169,60,0.18)',
  accentBorder: 'rgba(242,169,60,0.65)',
  success: '#176D3E',
  /* The mint kept, alpha raised with the other two tints for the same reason. */
  successSoft: 'rgba(23,109,62,0.1)',
  danger: '#AB3521',
  dangerFill: '#E8735F',
  dangerSoft: 'rgba(171,53,33,0.1)',
} as const;

/**
 * THE TOKENS EVERY COMPONENT USES — `var()` references, not colours.
 *
 * WHY THIS IS INDIRECTION AND NOT A PALETTE. There are ~1250 inline `style={{ color: c.text2 }}`
 * call sites across 138 files, and an inline style prop outranks every rule in the
 * stylesheet — the documented cause of more than one past layout defect in this repo. So a
 * light theme could not be a stylesheet that overrides them; it had to change what `c.text2`
 * evaluates to. Pointing each token at its custom property does that in one place: the
 * component code is untouched, and switching themes is one class on <html>.
 *
 * The properties themselves are declared twice in global.css — once on `:root` (dark, the
 * default, so the app is dark before any JS runs) and once under `[data-theme='light']`.
 * `theme.test.ts` asserts that both blocks declare every token here and that their values
 * are the DARK and LIGHT objects above, so a token cannot be added to one and forgotten in
 * the other.
 *
 * SVG PRESENTATION ATTRIBUTES, which is where this nearly came apart. lucide renders its
 * `color` prop as `stroke="…"` — an ATTRIBUTE on the <svg>, not a style declaration — and a
 * `var()` in an attribute is not resolved by every engine; where it is not, the value is
 * dropped and the icon paints black, which on the dark theme is an invisible icon. There are
 * 210 `color={c.token}` sites across 88 files, so editing them was not the answer either.
 *
 * What resolves it is that a presentation attribute has specificity ZERO — it loses to any
 * author rule at all. Since the attribute holds the literal text `var(--lyx-text2)`, an
 * attribute selector can match it and re-declare the same thing as a real declaration, where
 * `var()` always resolves. Six rules in global.css (`[stroke='var(--lyx-text2)']` and so on,
 * under `SVG ICON COLOURS`) cover all 210 sites, and no component was touched.
 *
 * ANYTHING OUTSIDE THE DOCUMENT still needs a literal: print windows and the licence
 * certificate build their own documents with no `:root` to inherit from — see lib/printBrand,
 * which has always carried its own ink-on-paper palette for exactly this reason. `DARK` and
 * `LIGHT` above are exported so those callers can take a real value.
 */
export const c = {
  bg: 'var(--lyx-bg)',
  surface1: 'var(--lyx-surface1)',
  surface2: 'var(--lyx-surface2)',
  surface3: 'var(--lyx-surface3)',
  border: 'var(--lyx-border)',
  borderStrong: 'var(--lyx-border-strong)',
  text1: 'var(--lyx-text1)',
  text2: 'var(--lyx-text2)',
  text3: 'var(--lyx-text3)',
  /** The amber you write with — see ACCENT AS INK VS ACCENT AS FILL under `LIGHT`. */
  accent: 'var(--lyx-accent)',
  /** The amber you fill with. Brand #F2A93C on both themes; `#1A1300` is its ink. */
  accentFill: 'var(--lyx-accent-fill)',
  accentSoft: 'var(--lyx-accent-soft)',
  accentBorder: 'var(--lyx-accent-border)',
  success: 'var(--lyx-success)',
  /** The soft mint behind a "Live" badge or a completed step. Never text. */
  successSoft: 'var(--lyx-success-soft)',
  /** Destructive text and icons. */
  danger: 'var(--lyx-danger)',
  /** A destructive FILL; `#2A0A05` is its ink. Only ConfirmDialog's confirm button. */
  dangerFill: 'var(--lyx-danger-fill)',
  dangerSoft: 'var(--lyx-danger-soft)',
} as const;

/**
 * Opacity applied to a disabled control, shared by Button and IconButton.
 *
 * Was 0.5 in the prototype, which is the value most component libraries use and
 * which is too low here. The fade multiplies the whole control, text included, so
 * it alone decides whether a disabled label is readable — and a disabled control
 * still names the action you cannot currently take. On surface1 the old value put
 * a disabled ghost label at 2.65:1 and a disabled icon at 2.65:1; both looked like
 * a rendering fault rather than a state.
 *
 * 0.8 is the highest-contrast case's floor: a ghost button's text2 label reaches
 * 4.74:1, a primary's 6.30:1, a secondary's 8.52:1. It is a smaller fade than
 * before, so it is paired with `cursor: not-allowed` and the real `disabled`
 * attribute rather than carrying the whole signal itself.
 */
export const DISABLED_OPACITY = 0.8;

/** The design-token palette. */
export type Theme = typeof c;

/** A valid token name, e.g. 'accent' | 'surface1'. */
export type ThemeToken = keyof Theme;
